import type { Store } from '../data/stores';
import { entraEnModo, ModoTiendas } from './modoTiendas';
import { storeChainBranch } from '../data/stores';
import type { SucursalRow } from '../types/indicadores.types';
import { rowKey } from './aggregate';

/**
 * Fila YA unida (join `Store` + `SucursalRow` del periodo) que consume
 * `TablaSucursales`: agrega los campos de presentación (`name`/`sub`) y
 * calcula `tasa` (venta/solicitudes, fracción 0..1) porque `GET
 * /dashboard/sucursales` NO trae un `pctTasa` precalculado por fila (a
 * diferencia de `GET /dashboard/tienda`, que sí expone `pctTasa` en su
 * bloque `hoy`/`ayer`) — mismo cálculo que usa `aggregateGlobal` para el
 * medidor "Tasa de atención" del embudo, aplicado por sucursal en vez de al
 * total del periodo.
 */
export interface SucursalTableRow {
  /** `Store.id` del catálogo — lo que recibe `onOpenStore` al hacer clic en la fila. */
  storeId: string;
  name: string;
  sub: string;
  consultas: number;
  solicitudes: number;
  atendidas: number;
  negados: number;
  /** Pares vendidos (Proscai cacheado); `null` si Proscai no respondió. */
  venta: number | null;
  /** Monto $ vendido de contado/crédito (ítem 2 2026-07-28); `null` si Proscai no respondió. */
  contadoMonto: number | null;
  creditoMonto: number | null;
  /**
   * Monto total (contado + crédito). Lo usa la tarjeta de TELÉFONO, que no
   * tiene sitio para las dos columnas de dinero: sin él, en un teléfono no se
   * podía ver cuánto vendió una tienda. `null` si Proscai no respondió.
   */
  montoTotal: number | null;
  /** Fracción 0..1: `venta / solicitudes atendidas` (ítem 8; `null` si no aplica). */
  tasa: number | null;
  /** Fracción 0..1, tal como llega de la API (`pctEfectividad`). */
  efect: number | null;
  /** Fracción 0..1, tal como llega de la API (`pctSla`). */
  sla: number | null;
  /** Segundos (`tiempoPromedio`); `null` si no hubo atendidas en el rango. */
  tiempo: number | null;
  /**
   * Tickets (SPEC 2026-10-13, d): sólo van al Excel. `null` = no se saben;
   * `undefined` = API anterior.
   */
  tickets?: number | null;
  /**
   * `true` = el renglón es de una tienda que ningún catálogo ubica (SPEC
   * 2026-10-13, b): se muestra «(fuera de catálogo)» y no abre la vista Tienda.
   */
  fueraDeCatalogo?: boolean;
}

/** Prefijo de `storeId` de un renglón fuera de catálogo: no es un `Store.id`. */
export const PREFIJO_FUERA_DE_CATALOGO = 'fuera:';

/** Top N de sucursales que muestra la tabla (línea 391/394 del handoff). */
export const SUCURSALES_TABLE_TOP_N = 40;

export interface SucursalTableResult {
  /** Ya recortadas al top N y ordenadas desc por `consultas`. */
  rows: SucursalTableRow[];
  /**
   * TODAS las filas unidas (mismo orden, SIN recortar al top N). Alimenta el
   * export a Excel y el modo "expandir" de la tabla (ítem 5, 2026-07-28): la
   * tabla en pantalla se queda con el top N por legibilidad, pero el usuario
   * pidió poder llevarse/ver el detalle completo.
   */
  rowsAll: SucursalTableRow[];
  /** Total de sucursales unidas ANTES de recortar (para la nota "Mostrando N de M"). */
  total: number;
}

/**
 * Une `sucursalRows` (respuesta cruda de `/dashboard/sucursales`) con
 * `filteredStores` (la cascada Regional/Supervisor/Cadena/Tienda) por
 * `(chain, branchOffice)` — mismo `rowKey` que usa `aggregateGlobal` — y
 * arma las filas de presentación de `TablaSucursales`: `consultas` se
 * recalcula como `solicitudes + negados` (NUNCA se confía en el campo
 * `consultas` de la fila cruda, mismo criterio que `aggregateGlobal` —
 * ver `utils/aggregate.test.ts`), se ordenan desc por esa `consultas` y se
 * recortan al top 40 (línea 391/394 del handoff).
 *
 * `soloActivas` (mismo switch "Activas en la herramienta" que consume
 * `aggregateGlobal`, default `false`): cuando es `true` descarta las filas
 * con `atendidas <= 0` ANTES de armar/ordenar/recortar — así `total` (usado
 * por la nota de pie "Mostrando N de M") ya refleja solo las sucursales que
 * de verdad usan la herramienta.
 */
export function buildSucursalTableRows(
  sucursalRows: SucursalRow[],
  filteredStores: Store[],
  modo: ModoTiendas = 'todas',
  /**
   * Cascada vacía («Todas», SPEC 2026-10-13, punto b): un renglón de la API sin
   * tienda en el catálogo efectivo NO se tira —su venta está en el total de
   * Global—: sale como «<nombre> (fuera de catálogo)», con «(sin regional)».
   */
  sinCascada = false,
): SucursalTableResult {
  const storeByKey = new Map<string, Store>();
  for (const store of filteredStores) {
    const { chain, branchOffice } = storeChainBranch(store);
    storeByKey.set(rowKey(chain, branchOffice), store);
  }

  const built: SucursalTableRow[] = [];
  for (const row of sucursalRows) {
    const store =
      storeByKey.get(rowKey(row.chainId, row.branchOfficeId)) ??
      (sinCascada ? tiendaFueraDeCatalogo(row) : undefined);
    if (!store) {
      continue;
    }
    if (!entraEnModo(row.atendidas, modo)) {
      continue;
    }

    const consultas = row.solicitudes + row.negados;
    // Ítem 8 (2026-07-28): tasa = venta ÷ solicitudes ATENDIDAS (antes ÷ solicitudes).
    const tasa =
      row.atendidas > 0 && row.paresVendidos !== null && row.paresVendidos !== undefined
        ? row.paresVendidos / row.atendidas
        : null;

    built.push({
      storeId: store.id,
      name: store.tienda,
      sub: `${store.cadena} · ${store.supervisor}`,
      consultas,
      solicitudes: row.solicitudes,
      atendidas: row.atendidas,
      negados: row.negados,
      venta: row.paresVendidos,
      contadoMonto: row.ventaContadoMonto,
      creditoMonto: row.ventaCreditoMonto,
      montoTotal:
        row.ventaContadoMonto === null && row.ventaCreditoMonto === null
          ? null
          : (row.ventaContadoMonto ?? 0) + (row.ventaCreditoMonto ?? 0),
      tasa,
      efect: row.pctEfectividad,
      sla: row.pctSla,
      tiempo: row.tiempoPromedio,
      tickets: row.tickets,
      ...(store.id.startsWith(PREFIJO_FUERA_DE_CATALOGO) ? { fueraDeCatalogo: true } : {}),
    });
  }

  // ⚠️ LAS TIENDAS SIN FILA TAMBIÉN SON TIENDAS (2026-09-07).
  //
  // `sucursalRows` trae sólo las tiendas con eventos o con venta en el
  // periodo. Una tienda del catálogo que no hizo NI una consulta NI vendió no
  // viene, y por eso el chip decía «7 tiendas sin uso» y la tabla pintaba
  // menos: el chip resta sobre el catálogo, la tabla recorría la respuesta.
  // En «sin uso» y «todas» se completan con ceros — son precisamente las que
  // hay que ver. En «activas» no aplica: sin fila no hay atendidas.
  if (modo !== 'activas') {
    const conFila = new Set(sucursalRows.map((r) => rowKey(r.chainId, r.branchOfficeId)));
    // Si Proscai contestó para las demás, un cero de venta es un cero real;
    // si TODAS vienen sin venta, tampoco se inventa un cero aquí.
    const proscaiRespondio = sucursalRows.some((r) => r.paresVendidos !== null);
    // Tickets de una tienda sin renglón: 0 si la API los sabe, «N/D» si algún
    // renglón no los sabe, y nada contra una API anterior (SPEC 2026-10-13, d).
    const ticketsRelleno = !sucursalRows.some((r) => r.tickets !== undefined)
      ? undefined
      : proscaiRespondio && !sucursalRows.some((r) => r.tickets === null)
        ? 0
        : null;
    for (const store of filteredStores) {
      const { chain, branchOffice } = storeChainBranch(store);
      if (conFila.has(rowKey(chain, branchOffice))) continue;
      built.push({
        storeId: store.id,
        name: store.tienda,
        sub: `${store.cadena} · ${store.supervisor}`,
        consultas: 0,
        solicitudes: 0,
        atendidas: 0,
        negados: 0,
        venta: proscaiRespondio ? 0 : null,
        contadoMonto: proscaiRespondio ? 0 : null,
        creditoMonto: proscaiRespondio ? 0 : null,
        montoTotal: proscaiRespondio ? 0 : null,
        tasa: null,
        efect: null,
        sla: null,
        tiempo: null,
        tickets: ticketsRelleno,
      });
    }
  }

  built.sort((a, b) => b.consultas - a.consultas);

  return {
    rows: built.slice(0, SUCURSALES_TABLE_TOP_N),
    rowsAll: built,
    total: built.length,
  };
}

/**
 * La «tienda» de un renglón que ningún catálogo ubica (SPEC 2026-10-13, punto
 * b): el nombre que manda la API más «(fuera de catálogo)», «(sin regional)».
 * Su `id` lleva `PREFIJO_FUERA_DE_CATALOGO`: no abre la vista Tienda.
 */
function tiendaFueraDeCatalogo(row: SucursalRow): Store {
  const nombre = (row.branchOfficeName ?? '').trim() || `${row.chainId} ${row.branchOfficeId}`;
  return {
    id: `${PREFIJO_FUERA_DE_CATALOGO}${row.chainId}#${row.branchOfficeId}`,
    cadena: row.chainId,
    tienda: `${nombre} (fuera de catálogo)`,
    num: String(row.branchOfficeId),
    supervisor: '(sin regional)',
    regional: '(sin regional)',
  };
}
